import * as Y from 'yjs';
import type { CanvasDoc, Frame, Viewport, ThemeToken, Tour, CommentThread } from './types';

/** Transaction origin tag for local edits (so Y.UndoManager tracks only ours). */
export const LOCAL_ORIGIN = Symbol('codeframe-local');

export interface YDocMaps {
  frames: Y.Map<Frame>;
  comments: Y.Map<CommentThread>;
  themes: Y.Map<ThemeToken>;
  tours: Y.Map<Tour>;
  scalars: Y.Map<unknown>;
}

/** Get the (lazily-created) collection maps of a Y.Doc. */
export function ydocMaps(ydoc: Y.Doc): YDocMaps {
  return {
    frames: ydoc.getMap('frames'),
    comments: ydoc.getMap('comments'),
    themes: ydoc.getMap('themes'),
    tours: ydoc.getMap('tours'),
    scalars: ydoc.getMap('scalars'),
  };
}

/** Hydrate a fresh Y.Doc from a CanvasDoc. Viewport is NOT stored (per-user local). */
export function buildYDoc(doc: CanvasDoc): Y.Doc {
  const ydoc = new Y.Doc();
  const m = ydocMaps(ydoc);
  ydoc.transact(() => {
    for (const f of doc.frames) m.frames.set(f.id, f);
    for (const c of doc.comments ?? []) m.comments.set(c.id, c);
    for (const t of doc.themes ?? []) m.themes.set(t.id, t);
    for (const t of doc.tours ?? []) m.tours.set(t.id, t);
    if (doc.activeThemeId !== undefined) m.scalars.set('activeThemeId', doc.activeThemeId);
    if (doc.cfIdScheme !== undefined) m.scalars.set('cfIdScheme', doc.cfIdScheme);
  });
  return ydoc;
}

/** Rebuild a CanvasDoc from a Y.Doc. Frames are z-ordered (z then id) since a
 *  Y.Map is unordered and z is the authoritative paint order. Viewport is
 *  injected from the caller's local viewport. */
export function readYDoc(ydoc: Y.Doc, viewport: Viewport): CanvasDoc {
  const m = ydocMaps(ydoc);
  const frames = [...m.frames.values()].sort(
    (a, b) => ((a.z ?? 0) - (b.z ?? 0)) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
  const comments = [...m.comments.values()];
  const themes = [...m.themes.values()];
  const tours = [...m.tours.values()];
  const activeThemeId = m.scalars.get('activeThemeId') as string | undefined;
  const cfIdScheme = m.scalars.get('cfIdScheme') as 'hash' | undefined;
  const out: CanvasDoc = { version: 2, frames, viewport };
  if (comments.length) out.comments = comments;
  if (themes.length) out.themes = themes;
  if (tours.length) out.tours = tours;
  if (activeThemeId !== undefined) out.activeThemeId = activeThemeId;
  if (cfIdScheme !== undefined) out.cfIdScheme = cfIdScheme;
  return out;
}
